import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { creditPurchaseService } from "../services/creditPurchaseService";
import { creditPurchaseDisplayId } from "./CreditPurchaseCommonStore";
import { CreditPurchase } from "../../../api/graphql";
import { tenantDisplayId } from "../../tenant/store/TenantCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { tenantService } from "../../tenant/services/tenantService";
import { InitializeParams } from "../../../store/types";

export class CreditPurchaseViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = creditPurchaseService;
  creditPurchaseInstance: CreditPurchase | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  tenantService = tenantService;
  tenantSearchValue: string = "";
  tenantIdValue: string = "";
  tenantSelectedValue: { displayId: string; id: string } | null = null;
  quantityValue: number = 0;
  purchaseDateValue: string | null = null;
  expirationDateValue: string | null = null;
  unitPriceValue: number | undefined = undefined;
  notesValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadCreditPurchaseById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.creditPurchaseOne({ id });
    this.creditPurchaseInstance = result.creditPurchaseOne;
    if (this.creditPurchaseInstance) {
      this.loadValues(this.creditPurchaseInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.creditPurchaseInstance) {
      return creditPurchaseDisplayId(
        this.creditPurchaseInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.CreditPurchaseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "tenant") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.tenantOne({ id: params.returnData.resultId });
        const tenant = result.tenantOne;
        this.tenantSelectedValue = tenant;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get tenantValue() {
    if (this.tenantSelectedValue) {
      return {
        id: this.tenantSelectedValue.id,
        displayId: tenantDisplayId(this.tenantSelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.creditPurchaseInstance = null;
    this.tenantSearchValue = "";
    this.tenantIdValue = "";
    this.quantityValue = 0;
    this.purchaseDateValue = null;
    this.expirationDateValue = null;
    this.unitPriceValue = undefined;
    this.notesValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelCreditPurchase() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editCreditPurchase() {
    const id = this.creditPurchaseInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/creditPurchase/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printCreditPurchase(handlePrint: () => void): void {
    handlePrint();
  }

  *closeCreditPurchase(): any {
    yield this.close();
  }

  loadValues(instance: CreditPurchase) {
    this.idValue = instance.id;
    this.tenantSelectedValue = instance.tenant ? instance.tenant : null;
    this.quantityValue = instance.quantity;
    this.purchaseDateValue = diaDeCalendario(instance.purchaseDate);
    this.expirationDateValue = diaDeCalendario(instance.expirationDate);
    this.unitPriceValue = instance.unitPrice || undefined;
    this.notesValue = instance.notes || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadCreditPurchaseById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
